import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { COUNTRIES, findCountry, findRegion, STORAGE_KEY, REGION_KEY } from "@/lib/countries";
import CountrySelector from "@/components/CountrySelector";

const CountryContext = createContext(null);
export const useCountry = () => useContext(CountryContext);

const LEGAL_PATHS = ["/privacy-policy", "/privacy", "/terms", "/payment", "/founder-insights"];

export function CountryProvider({ children }) {
  const { pathname } = useLocation();
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const savedCountry = localStorage.getItem(STORAGE_KEY);
    const savedRegion = localStorage.getItem(REGION_KEY);
    const c = savedCountry ? findCountry(savedCountry) : null;
    const r = c ? findRegion(c, savedRegion) : null;
    const needsRegion = c?.subSelect && !r;
    const isLegal = LEGAL_PATHS.some((p) => pathname.startsWith(p));
    if (c && !needsRegion) {
      setCountry(c);
      setRegion(r);
    } else if (!isLegal) {
      setOpen(true);
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Finalise selection (country + optional state/province).
  const complete = (countryCode, regionCode) => {
    const c = findCountry(countryCode);
    if (!c || c.status === "soon") return;
    const r = findRegion(c, regionCode) || null;
    setCountry(c);
    setRegion(r);
    localStorage.setItem(STORAGE_KEY, c.code);
    if (r) localStorage.setItem(REGION_KEY, r.code);
    else localStorage.removeItem(REGION_KEY);
    setOpen(false);
  };

  const openSelector = () => setOpen(true);
  const closeSelector = () => country && setOpen(false);

  return (
    <CountryContext.Provider value={{ country, region, countries: COUNTRIES, openSelector, complete }}>
      {children}
      <AnimatePresence>
        {ready && open && (
          <CountrySelector
            current={country}
            currentRegion={region}
            onComplete={complete}
            onClose={country ? closeSelector : undefined}
          />
        )}
      </AnimatePresence>
    </CountryContext.Provider>
  );
}
